import {defineStore} from 'pinia';
import axios from 'axios';
import type {Magazine, PaginatedResults} from '~/types/types';
import useAuthStore from './auth';
import {defaultResponseStatus, handleApiRequest} from '~/functions/handleApiRequest';
import type {ResponseStatus} from '~/lms';

interface MagazineStoreState {
    paginatedMagazines: PaginatedResults<Magazine> | undefined;
    selectedMagazine: Magazine | undefined;
    lmsApiBaseurl: string;
    isFetchingMagazines: boolean;
    magazineResponseStatus: ResponseStatus;
}

interface MagazineStoreActions {
    /**
     * Fetch magazines with pagination
     */
    setMagazines: (page?: number, year?: number, season?: string) => void;
    
    /**
     * Set the selected magazine for modal display
     */
    setSelectedMagazine: (magazine: Magazine | undefined) => void;
    
    /**
     * Clear magazine data
     */
    clearMagazines: () => void;
}

const useMagazineStore = defineStore<string, MagazineStoreState, {}, MagazineStoreActions>(
    'magazine',
    {
        state: (): MagazineStoreState => {
            return {
                paginatedMagazines: undefined,
                selectedMagazine: undefined,
                lmsApiBaseurl: '',
                isFetchingMagazines: false,
                magazineResponseStatus: defaultResponseStatus,
            };
        },
        actions: {
            async setMagazines(pageNumber, year, season) {
                if (!useAuthStore().authHeaders) {
                    return;
                }

                if (this.isFetchingMagazines) {
                    return;
                }

                // Build query parameters
                const params: Record<string, string | number> = {};
                if (pageNumber !== undefined) {
                    params.page = pageNumber;
                }
                if (year !== undefined) {
                    params.year = year;
                }
                if (season !== undefined) {
                    params.season = season;
                }

                const result = await handleApiRequest(
                    this,
                    'isFetchingMagazines',
                    'magazineResponseStatus',
                    () =>
                        axios.get(`${this.lmsApiBaseurl}magazines`, {
                            headers: useAuthStore().authHeaders,
                            params,
                        }),
                    {ref: 'paginatedMagazines', value: undefined},
                );

                if (result) {
                    this.paginatedMagazines = result;
                }
            },
            
            setSelectedMagazine(magazine) {
                this.selectedMagazine = magazine;
            },
            
            clearMagazines() {
                this.paginatedMagazines = undefined;
                this.selectedMagazine = undefined;
                this.magazineResponseStatus = defaultResponseStatus;
            },
        },
    },
);

export default useMagazineStore;